import React, { useEffect, useState } from "react";
import axios from "axios";
import { Row, Col, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/core/basic/Button";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import Loading from "../../../components/core/blocks/Loading";
import { formatCurrency } from "../../../utils/formatNumber";
import PricingCharts from "../Charts";
import ListingQuality from "../../product_quality/components/ListingQuality";
import ExternalProductLink from "../../../components/custom/merchandise/ExternalProductLink";

const URL_PREFIX = getURLPrefix();

const ListingDetails = ({ productID, item, marketPlace, setShowContentDetails, showContentDetails }) => {
  const { t } = useTranslation(["pricingv2", "common"]);
  const [contentDetails, setContentDetails] = useState(null);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optData, setOptData] = useState(null);
  const [priceData, setPriceData] = useState({
    current: null,
    min: null,
    max: null,
    optimal: null,
  });

  const fetchDetails = async () => {
    let price = 0.0;
    if (item.price) {
      if (item.price.value) {
        price = item.price.value;
      } else if (item.price) {
        price = item.price;
      }
    }

    const url = `${URL_PREFIX}/api/explorer/product?asin=${item.asin}&marketplace=${marketPlace}&price=${price}`;
    const { data } = await axios.get(url, {});

    if (data) {
      setContentDetails(data.content_details);
      setListing(data.listing);
      setLoading(false);
    }
  };

  const fetchOrderHistory = async () => {
    const url = `${URL_PREFIX}/api/explorer/order_history?asin=${item.asin}&marketplace=${marketPlace}`;
    const { data } = await axios.get(url, {});

    if (data) {
      setOptData(data);
      getPricingData(data);
    }
  };

  useEffect(() => {
    if (item && marketPlace && showContentDetails) {
      fetchDetails();
      fetchOrderHistory();
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [item, productID, showContentDetails]);

  const hideModal = () => {
    setShowContentDetails(false);
    setContentDetails(null);
    setListing(null);
    setLoading(true);
    setOptData(null);
  };

  const getPricingData = (data) => {
    const priceData = {
      current: null,
      min: null,
      max: null,
      optimal: null,
    };

    if (data) {
      priceData.optimal = data.best_price;
      priceData.current = data.current_price;
      Object.keys(data.order_history).forEach((key) => {
        const item = data.order_history[key];
        if (priceData.min === null || item.price < priceData.min) {
          priceData.min = item.price;
        }
        if (priceData.max === null || item.price > priceData.max) {
          priceData.max = item.price;
        }
      });

      setPriceData(priceData);
    }
  };

  const getSpecValue = (value) => {
    if (value === true) {
      return t(($) => $.pricingv2["Yes"]);
    } else if (value === false) {
      return t(($) => $.pricingv2["No"]);
    }
    return value;
  };

  return (
    <Modal show={showContentDetails} onHide={() => hideModal()} size="xl">
      <Modal.Header closeButton className="py-0 px-5">
        {listing && (
          <Modal.Title
            id="contained-modal-title-lg"
            style={{
              whiteSpace: "break-spaces",
              maxWidth: "100%",
            }}
          >
            {listing.summaries[0].itemName}
          </Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body>
        {loading && <Loading text={t(($) => $.pricingv2["Loading Product Details"])} />}

        {contentDetails && (
          <>
            <Row>
              <Col xs={12} className="ps-3 pe-3">
                {!optData && (
                  <div
                    style={{
                      height: "270px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "14px",
                    }}
                  >
                    {t(($) => $.pricingv2["Loading Pricing Chart"])}
                  </div>
                )}

                {optData && (
                  <>
                    <Row className="fs-4 mb-4 mx-3">
                      <Col xs={6}>
                        <div className="fw-bold">{t(($) => $.pricingv2["Price History"])}</div>
                      </Col>
                      <Col xs={6} className="text-end">
                        <span className="fw-bold pe-2">{t(($) => $.pricingv2["ASIN:"])}</span>
                        {item.asin}
                        <ExternalProductLink asin={item?.asin} />
                      </Col>
                    </Row>

                    <PricingCharts optData={optData} marketPlace={marketPlace} height={270} />
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <Col xs={5} className="ps-5 pe-5">
                <div className="fs-4 pb-2" style={{ fontWeight: "bold" }}>
                  {t(($) => $.pricingv2["Pricing"])}
                </div>
                <Row className="pb-4 fs-4">
                  <Col xs={6}>
                    <div className="pb-2">{t(($) => $.pricingv2["Current Price"])}</div>
                  </Col>
                  <Col xs={6} className={`text-end`}>
                    {priceData["current"] ?
                      formatCurrency(priceData["current"], {
                        marketPlace: marketPlace,
                      })
                    : t(($) => $.pricingv2["N/A"])}
                  </Col>

                  <Col xs={6}>
                    <div className="pb-2">{t(($) => $.pricingv2["Lowest Price"])}</div>
                  </Col>
                  <Col xs={6} className={`text-end`}>
                    {priceData["min"] ?
                      formatCurrency(priceData["min"], {
                        marketPlace: marketPlace,
                      })
                    : t(($) => $.pricingv2["N/A"])}
                  </Col>
                  <Col xs={6}>
                    <div className="pb-2">{t(($) => $.pricingv2["Highest Price"])}</div>
                  </Col>
                  <Col xs={6} className={`text-end`}>
                    {priceData["max"] ?
                      formatCurrency(priceData["max"], {
                        marketPlace: marketPlace,
                      })
                    : t(($) => $.pricingv2["N/A"])}
                  </Col>
                </Row>

                {contentDetails && <ListingQuality contentDetails={contentDetails} />}
              </Col>
              <Col xs={7} className="ps-5 pe-5">
                <div className="fs-4 pb-2" style={{ fontWeight: "bold" }}>
                  {t(($) => $.pricingv2["Technical Detail"])}
                </div>
                <div style={{ maxHeight: "400px", overflowY: "auto", overflowX: "hidden", width: "100%" }}>
                  <Row
                    className="pb-2 pt-2"
                    style={{
                      fontSize: "14px",
                    }}
                  >
                    {listing &&
                      Object.keys(listing.attributes).map((spec_name, is) => {
                        const spec = listing.attributes[spec_name];

                        if (spec.length !== 1) {
                          return null;
                        }

                        if (!spec[0].value || spec[0].value.length > 30) {
                          return null;
                        }

                        return (
                          <React.Fragment key={is}>
                            <Col xs={6} className="pt-2">
                              <div
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "200px",
                                  margin: 0,
                                }}
                              >
                                <strong>
                                  {spec_name.replaceAll("_", " ").replaceAll(/\w\S*/g, function (txt) {
                                    return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
                                  })}
                                </strong>
                              </div>
                            </Col>
                            <Col xs={6}>{getSpecValue(spec[0].value)}</Col>
                          </React.Fragment>
                        );
                      })}
                  </Row>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Row className="mx-auto py-4">
          <Col className="text-center">
            <Button onPress={() => hideModal()}>{t(($) => $.common["Close"])}</Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

export default ListingDetails;
